import React from "react";

import Paper from "@material-ui/core/Paper";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PropTypes from "prop-types";

import RbPopper from "components/RbPopper";

import useStyles from "./RbActions.styles";
import RbActionsContext from "./RbActionsContext";

export const RbActions = React.forwardRef(function RbActions(props, ref) {
  const { compacted, limit, color, children, moreLabel } = props;

  const classes = useStyles();
  const moreButtonRef = React.useRef();
  const [moreOpen, setMoreOpen] = React.useState(false);

  const handleMoreClick = () => {
    setMoreOpen(true);
  };

  const handleCloseMore = React.useCallback(() => {
    setMoreOpen(false);
    if (moreButtonRef.current) {
      moreButtonRef.current.focus();
    }
  }, []);

  const visibleContextValue = React.useMemo(() => {
    return {
      compacted,
      insideMore: false,
      color,
    };
  }, [compacted, color]);

  const moreContextValue = React.useMemo(() => {
    return {
      compacted: false,
      insideMore: true,
      handleCloseMore,
      color,
    };
  }, [handleCloseMore, color]);

  const childrenArray = React.Children.toArray(children);
  const childrenVisible = childrenArray.slice(0, limit);
  const childrenMore = childrenArray.slice(limit);

  return (
    <div ref={ref} className={classes.root}>
      <RbActionsContext.Provider value={visibleContextValue}>
        {childrenVisible}
      </RbActionsContext.Provider>
      {childrenMore.length > 0 && (
        <button
          ref={moreButtonRef}
          className={classes.moreButton}
          type="button"
          onClick={handleMoreClick}
          aria-expanded={moreOpen}
          aria-label={moreLabel}
        >
          <MoreVertIcon
            className={`${classes.moreIcon} ${classes[`moreIcon_${color}`]}`}
          />
        </button>
      )}
      <RbPopper
        open={moreOpen}
        anchorEl={moreButtonRef.current}
        placement="bottom-end"
        onClose={handleCloseMore}
      >
        <RbActionsContext.Provider value={moreContextValue}>
          <Paper>{childrenMore}</Paper>
        </RbActionsContext.Provider>
      </RbPopper>
    </div>
  );
});

RbActions.propTypes = {
  /** Components of type RbButtonAction, RbMenuAction and RbSearchAction. */
  children: PropTypes.node,
  /** Color of the actions. */
  color: PropTypes.oneOf(["primary", "default"]),
  /** Show only the icons of actions. */
  compacted: PropTypes.bool,
  /** Maximum number of actions that will be showed. If there are more they will be showed in the more button.  */
  limit: PropTypes.number,
  /** The icons more's label. */
  moreLabel: PropTypes.string,
};

RbActions.defaultProps = {
  color: "primary",
  children: null,
  limit: 3,
  compacted: false,
  moreLabel: "More actions",
};

RbActions.displayName = "RbActions";

export default React.memo(RbActions);
